import React from "react";
import gettext from "../../shared/utils/gettext";
// @ts-ignore
import DatePicker from "react-datepicker";
import moment from "moment";
import CreatableSelect from "react-select/creatable";
import "./Finance.scss";
import cropr, { CropFieldModel } from "../../shared/utils/cropr";

type CostOrRevenue = "cost" | "revenue";

interface CostRevenueType {
  type: CostOrRevenue;
  value: string | null; // e.g. 'Lease'
  price: number; //price
  std: boolean; // standard or user-defined value
  date?: string; // date string
}

interface CostRevenueTypeInput extends CostRevenueType {
  id: number;
  priceInput: string;
  pricePerHaInput: string;
}

// helper function for getting cost/revenue type lists for countries
function getCostRevenueTypesForCountry(type: CostOrRevenue, countryCode: string): LabelValue[] {
  // see if we have it in cache
  const cacheIndex = type + countryCode;
  // build list of translated labels and values for this type and countryCode
  let result = cropr.cropfieldCostRevenueTypesForCountry[cacheIndex];
  if (!result) {
    result = cropr.data.cropfieldCostRevenueTypes
      .filter(
        (ct: any) =>
          ct.type === type && (ct.countries.length === 0 || ct.countries.includes(countryCode))
      )
      .map((ct: any) => ({ label: gettext(ct.id), value: ct.id }));
    // cache this result
    cropr.cropfieldCostRevenueTypesForCountry[cacheIndex] = result;
  }
  return result;
}

// helper function
function valueIsStandardType(type: string, value: string | null) {
  const countries = cropr.farm.get("countries");
  for (const country of countries.split(",")) {
    if (
      cropr.data.cropfieldCostRevenueTypes.some(
        (ct: any) =>
          ct.type === type &&
          ct.id === value &&
          (ct.countries.length === 0 || ct.countries.includes(country))
      )
    )
      return true;
  }
  return false;
}

interface RowProps {
  item: CostRevenueTypeInput;
  typeList: LabelValue[];
  ha: number;
  handleChange: (id: number, field: string, value: any) => void;
  remove: (id: number) => void;
}

function getLabel(item: CostRevenueType) {
  return item.std && item.value ? gettext(item.value || "") : item.value;
}
const Row = ({ item, typeList, ha, handleChange, remove }: RowProps) => (
  <tr>
    <td>
      <CreatableSelect
        id={item.type + "Name" + item.id}
        options={typeList}
        onChange={(val: any) => {
          $("#" + item.type + "Name" + item.id).removeClass("is-invalid");
          handleChange(item.id, "name", val);
        }}
        value={{ label: getLabel(item), value: item.value }}
        className="react-select"
      />
    </td>
    <td>
      <input
        id={item.type + "ValuePerHa" + item.id}
        className="form-control number text-right"
        value={item.pricePerHaInput}
        onChange={(event) => handleChange(item.id, "pricePerHa", event.target.value)}
        disabled={ha === 0.0}
      />
    </td>
    <td>
      <input
        id={item.type + "Value" + item.id}
        className="form-control number text-right"
        value={item.priceInput}
        onChange={(event) => {
          $("#" + item.type + "Value" + item.id).removeClass("is-invalid");
          handleChange(item.id, "price", event.target.value);
        }}
      />
    </td>
    <td>
      <DatePicker
        id={item.type + "Date" + item.id}
        className="form-control"
        type="date"
        selected={(item.date && new Date(item.date)) || null}
        dateFormat="dd-MM-yyyy"
        onChange={(date: Date) => {
          // DatePicker uses Date, value "28-02-2021" is stringified to e.g.
          // "2021-02-27T23:00:00.000Z", i.e. one hour off. Use moment to convert to YYYY-MM-DD
          handleChange(item.id, "date", (date && moment(date, "YYYY-MM-DD")) || null);
        }}
      />
    </td>
    <td>
      <button
        type="button"
        className="btn text-yellow"
        title={gettext("Delete")}
        onClick={() => remove(item.id)}
      >
        <i className="far fa-trash-alt" />
      </button>
    </td>
  </tr>
);

interface CostOrRevenueTableProps {
  type: CostOrRevenue;
  items: CostRevenueTypeInput[];
  typeList: LabelValue[];
  ha: number;
  hNew: (type: CostOrRevenue) => void;
  hChange: (id: number, field: string, value: any) => void;
  hRemove: (id: number) => void;
}

const CostOrRevenueTable = ({
  type,
  items,
  typeList,
  ha,
  hNew,
  hChange,
  hRemove,
}: CostOrRevenueTableProps) => {
  const sumOfPrices = items
    .filter((item) => item.type === type)
    .map((item) => item.price)
    .reduce((a, b) => (a || 0) + (b || 0), 0.0);
  return (
    <table className="table">
      <thead>
        <tr>
          <th className="col-width-30">
            {type === "cost" ? gettext("Costs") : gettext("Revenues")}
          </th>
          <th>
            {cropr.l10n.unit_currency_sign}/{cropr.l10n.unit_area_display}
          </th>
          <th>{gettext("Total")}</th>
          <th>{gettext("Date")}</th>
        </tr>
      </thead>
      <tbody>
        {items
          .filter((item: any) => item.type === type)
          .map((item: any) => (
            <Row
              key={item.type + "Row" + item.id}
              item={item}
              typeList={typeList}
              ha={ha}
              handleChange={hChange}
              remove={hRemove}
            />
          ))}
        <tr>
          <td>
            <button type="button" className="btn btn-green" onClick={() => hNew(type)}>
              <i className="far fa-plus pr-2" />
              {type === "cost" ? gettext("Cost") : gettext("Revenue")}
            </button>
          </td>
        </tr>
        <tr>
          <td>{gettext("Total")}</td>
          <td className="grey-border-top text-right p-r">
            <div id="finance_cost_total_per_ha">
              {cropr.l10n.unit_currency_sign}&nbsp;
              {cropr.toLocaleFixed(ha > 0 ? sumOfPrices / ha : 0.0, 2, true)}
            </div>
          </td>
          <td className="grey-border-top text-right">
            <div id="finance_cost_total">
              {cropr.l10n.unit_currency_sign}&nbsp;
              {cropr.toLocaleFixed(sumOfPrices, 2, true)}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
interface LabelValue {
  label: string;
  value: string;
}
interface CropFieldFinanceProps {
  cropfield: CropFieldModel;
  cropFieldCostsRevenues: CostRevenueType[];
  onChange: (data: CostRevenueType[]) => void;
  costTypes: string[];
  revenueTypes: string[];
}
interface CropFieldFinanceState {
  ha: number;
  items: CostRevenueTypeInput[];
  costTypes: LabelValue[];
  revenueTypes: LabelValue[];
}
class CropFieldFinance extends React.Component<CropFieldFinanceProps, CropFieldFinanceState> {
  constructor(props: CropFieldFinanceProps) {
    super(props);
    const ha = this.props.cropfield.get("registered_area") || 0.0;
    // convert price and pricePerHa to locale strings
    const items =
      this.props.cropFieldCostsRevenues?.map?.(({ price, ...p }, id) => ({
        pricePerHaInput: ha > 0.0 ? cropr.toLocaleFixed(price / ha, 2) : "",
        priceInput: cropr.toLocaleFixed(price, 2),
        price,
        id,
        ...p,
      })) || [];
    const cfCountryCode = this.props.cropfield.get("country");
    const myCostTypes = this.props.costTypes.map((t) => ({ label: t, value: t }));
    const costTypes = getCostRevenueTypesForCountry("cost", cfCountryCode);
    const myRevTypes = this.props.revenueTypes.map((t) => ({ label: t, value: t }));
    const revTypes = getCostRevenueTypesForCountry("revenue", cfCountryCode);
    this.state = {
      ha,
      items,
      costTypes: [...myCostTypes, ...costTypes],
      revenueTypes: [...myRevTypes, ...revTypes],
    };
  }

  save = (items: CostRevenueTypeInput[]) => {
    // convert price to a float
    this.props.onChange(
      items.map(({ priceInput, pricePerHaInput, id, price, date, ...p }) => {
        const e: CostRevenueType = {
          price: priceInput === "" ? 0.0 : parseFloat(priceInput.replace(",", ".")),
          ...p,
        };
        if (date) e.date = date;
        return e;
      })
    );
  };
  handleRemove = (id: number) => {
    const items = this.state.items.filter((item) => item.id !== id);
    this.setState({ items });
    this.save(items);
  };
  handleNewItem = (type: CostOrRevenue) => {
    const items = this.state.items;
    const newId = items.length === 0 ? 0 : items[items.length - 1].id + 1;
    const newItems = items.concat({
      id: newId,
      type: type,
      value: "",
      price: 0,
      priceInput: "",
      pricePerHaInput: "",
      std: true,
    });
    this.setState({ items: newItems });
    this.save(newItems);
  };
  handleChange = (id: number, field: string, value: any) => {
    const items = this.state.items.map((d) => {
      const c = { ...d };
      if (c.id === id) {
        if (field === "price") {
          c.priceInput = value;
          c.price = parseFloat(value.replace(",", "."));
          // total price was changed by user, set price per ha accordingly
          if (this.state.ha > 0.0) {
            c.pricePerHaInput = cropr.toLocaleFixed(c.price / this.state.ha, 2);
          }
        }
        if (field === "pricePerHa") {
          c.pricePerHaInput = value;
          c.price = parseFloat(value.replace(",", ".")) * this.state.ha;
          // price per ha changed by user, set the total price accordingly
          c.priceInput = cropr.toLocaleFixed(c.price, 2);
        }
        if (field === "name") {
          c.value = value ? value.value : null;
          // set the std flag. If not std, the value will be added to the list of user-defined
          // types in farmsettings.
          c.std = !value || valueIsStandardType(c.type, c.value);
        }
        if (field === "date") {
          c.date = value;
        }
      }
      return c;
    });
    this.setState({ items: items });
    this.save(items);
  };

  render() {
    const ha = this.props.cropfield.get("registered_area") || 0.0;
    return (
      <div>
        <h4>
          {gettext("Cropfield")} {this.props.cropfield.get("name")} ({cropr.toLocaleFixed(ha, 2)}{" "}
          {cropr.l10n.unit_area_display})
        </h4>
        <div className="row">
          <div className="col-6 b-r border-dark">
            <CostOrRevenueTable
              type="cost"
              items={this.state.items}
              typeList={this.state.costTypes}
              ha={ha}
              hNew={this.handleNewItem}
              hChange={this.handleChange}
              hRemove={this.handleRemove}
            />
          </div>
          <div className="col-6">
            <CostOrRevenueTable
              type="revenue"
              items={this.state.items}
              typeList={this.state.revenueTypes}
              ha={ha}
              hNew={this.handleNewItem}
              hChange={this.handleChange}
              hRemove={this.handleRemove}
            />
          </div>
        </div>
      </div>
    );
  }
}
export default CropFieldFinance;
